import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Textarea from "../../components/common/Textarea";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import Table from "../../components/common/Table";
import Badge from "../../components/common/Badge";
import Select from "../../components/common/Select";
import ImageWithPlaceholder from "../../components/common/ImageWithPlaceholder";
import { getFullImageUrl } from "../../utils/imageUrl";
import { useStationFeature } from "../../hooks/useStationFeature";
import {
  getStationErrorMessage,
  shouldRefreshStations,
} from "../../utils/stationErrorHandler";
import { getCatalogOwnershipMessage } from "../../utils/catalogOwnership";
import {
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useGetItemsQuery,
  usePatchCategorySortOrderMutation,
} from "../../store/api";

const Categories = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useGetCategoriesQuery();
  const { data: itemsData } = useGetItemsQuery();
  const [createCategory, createState] = useCreateCategoryMutation();
  const [updateCategory, updateState] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();
  const [patchCategorySortOrder] = usePatchCategorySortOrderMutation();
  const { stationFeatureActive, printTokensByStation, activeStations, refetchStations } =
    useStationFeature();

  const [form, setForm] = useState({
    name: "",
    description: "",
    sort: 0,
    isActive: true,
    parentCategoryId: "",
    defaultStationCode: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    categoryId: null,
    categoryName: "",
  });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });
  const [pendingSortByCategory, setPendingSortByCategory] = useState({});
  const [savingSortByCategory, setSavingSortByCategory] = useState({});
  const [sortSuccessMessage, setSortSuccessMessage] = useState("");
  const sortSuccessTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (sortSuccessTimeoutRef.current) {
        clearTimeout(sortSuccessTimeoutRef.current);
      }
    };
  }, []);

  const categories = Array.isArray(data) ? data : [];
  const items = Array.isArray(itemsData) ? itemsData : [];

  const filteredCategories = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const base = !query
      ? categories
      : categories.filter((category) => {
      const haystack = `${category.name || ""} ${category.description || ""}`
        .trim()
        .toLowerCase();
      return haystack.includes(query);
    });
    return [...base].sort(
      (a, b) =>
        (Number(a.sort ?? 0) - Number(b.sort ?? 0)) ||
        (a.name || "").localeCompare(b.name || ""),
    );
  }, [categories, searchQuery]);

  // Count items per category
  const itemCountByCategory = useMemo(() => {
    const counts = new Map();
    items.forEach((item) => {
      const count = counts.get(item.categoryId) || 0;
      counts.set(item.categoryId, count + 1);
    });
    return counts;
  }, [items]);

  // Organize categories into parent-child structure
  const { parentCategories, categoryMap } = useMemo(() => {
    const map = new Map();
    const parents = [];

    categories.forEach((cat) => {
      map.set(cat.id, cat);
      if (!cat.parentCategoryId) {
        parents.push(cat);
      }
    });

    return { parentCategories: parents, categoryMap: map };
  }, [categories]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match(/image\/(png|jpg|jpeg)/)) {
        setErrorModal({
          isOpen: true,
          message: "Only PNG and JPG images are allowed",
        });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setErrorModal({
          isOpen: true,
          message: "File size must be less than 2MB",
        });
        return;
      }
      setImageFile(file);
      setRemoveImage(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        sort: Number(form.sort),
        parentCategoryId: form.parentCategoryId || null,
        defaultStationCode: stationFeatureActive
          ? form.defaultStationCode || null
          : undefined,
      };
      if (editingId) {
        await updateCategory({
          categoryId: editingId,
          payload,
          file: imageFile,
          removeImage,
        }).unwrap();
        setEditingId(null);
      } else {
        await createCategory({
          payload,
          file: imageFile,
        }).unwrap();
      }
      setForm({
        name: "",
        description: "",
        sort: 0,
        isActive: true,
        parentCategoryId: "",
      });
      setShowForm(false);
      setImageFile(null);
      setImagePreview(null);
      setRemoveImage(false);
    } catch (err) {
      if (shouldRefreshStations(err)) {
        refetchStations();
      }
    }
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    setForm({
      name: category.name || "",
      description: category.description || "",
      sort: category.sort || 0,
      isActive: category.isActive !== undefined ? category.isActive : true,
      parentCategoryId: category.parentCategoryId || "",
      defaultStationCode: category.defaultStationCode || "",
    });
    setImagePreview(getFullImageUrl(category.imageUrl));
    setImageFile(null);
    setRemoveImage(false);
    setShowForm(true);
  };

  const handleDelete = async (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    setDeleteModal({
      isOpen: true,
      categoryId,
      categoryName: category?.name || "this category",
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteCategory(deleteModal.categoryId).unwrap();
      setDeleteModal({ isOpen: false, categoryId: null, categoryName: "" });
    } catch (err) {
      setDeleteModal({ isOpen: false, categoryId: null, categoryName: "" });
      const errorMessage =
        err?.data?.detail ||
        err?.data?.message ||
        "Failed to delete category. It may have items associated with it.";
      setErrorModal({ isOpen: true, message: errorMessage });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm({
      name: "",
      description: "",
      sort: 0,
      isActive: true,
      parentCategoryId: "",
      defaultStationCode: "",
    });
    setShowForm(false);
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
  };

  const getCategorySortInputValue = (category) => {
    if (Object.prototype.hasOwnProperty.call(pendingSortByCategory, category.id)) {
      return pendingSortByCategory[category.id];
    }
    return String(Number(category.sort ?? 0));
  };

  const handleCategorySortInputChange = (categoryId, value) => {
    if (!/^\d*$/.test(value)) return;
    setPendingSortByCategory((prev) => ({
      ...prev,
      [categoryId]: value,
    }));
  };

  const handleSaveCategorySort = async (category) => {
    const rawValue = getCategorySortInputValue(category);
    if (rawValue === "") {
      setErrorModal({ isOpen: true, message: "Sort order must be >= 0." });
      return;
    }
    const sortOrder = Number(rawValue);
    if (!Number.isInteger(sortOrder) || sortOrder < 0) {
      setErrorModal({ isOpen: true, message: "Sort order must be >= 0." });
      return;
    }
    if (sortOrder === Number(category.sort ?? 0)) return;
    setSavingSortByCategory((prev) => ({ ...prev, [category.id]: true }));
    try {
      await patchCategorySortOrder({ categoryId: category.id, sortOrder }).unwrap();
      setPendingSortByCategory((prev) => {
        const next = { ...prev };
        delete next[category.id];
        return next;
      });
      setSortSuccessMessage(`Display order for ${category.name} updated successfully.`);
      if (sortSuccessTimeoutRef.current) {
        clearTimeout(sortSuccessTimeoutRef.current);
      }
      sortSuccessTimeoutRef.current = setTimeout(() => {
        setSortSuccessMessage("");
        sortSuccessTimeoutRef.current = null;
      }, 3000);
    } catch (err) {
      setErrorModal({
        isOpen: true,
        message:
          err?.data?.detail ||
          "Sort order is already assigned. Choose another number.",
      });
    } finally {
      setSavingSortByCategory((prev) => ({ ...prev, [category.id]: false }));
    }
  };

  const columns = [
    { key: "name", label: "Name", headerClassName: "py-1 text-[11px]", cellClassName: "py-0.5 text-xs" },
    {
      key: "parent",
      label: "Parent Category",
      headerClassName: "py-1 text-[11px]",
      cellClassName: "py-0.5 text-xs",
    },
    { key: "items", label: "Items", headerClassName: "py-1 text-[11px]", cellClassName: "py-0.5 text-xs" },
    {
      key: "sort",
      label: "Sort Order",
      headerClassName: "py-1 text-[11px]",
      cellClassName: "py-0.5 text-xs",
    },
    {
      key: "status",
      label: "Status",
      headerClassName: "py-1 text-[11px]",
      cellClassName: "py-0.5 text-xs",
    },
  ];

  const rows = filteredCategories.map((category, index) => {
    const parentCategory = category.parentCategoryId
      ? categoryMap.get(category.parentCategoryId)
      : null;

    const itemCount = itemCountByCategory.get(category.id) || 0;

    return {
      id: category.id,
      className: [
        "border-t border-slate-200/80 dark:border-slate-700",
        index % 2 === 0 ? "bg-white dark:bg-slate-800" : "bg-slate-50/50 dark:bg-slate-800/70",
        "hover:bg-slate-100/70 dark:hover:bg-slate-700/60",
      ].join(" "),
      name: (
        <div className="flex items-center gap-1.5">
          <ImageWithPlaceholder
            src={category.imageUrl}
            alt={category.name}
            size="xs"
          />
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {category.name}
            </div>
            {category.description && (
              <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                {category.description}
              </div>
            )}
          </div>
        </div>
      ),
      parent: parentCategory ? (
        <div className="flex items-center gap-2">
          <svg
            className="h-4 w-4 text-slate-400 dark:text-slate-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
          <span className="text-xs text-slate-600 dark:text-slate-400">
            {parentCategory.name}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="inline-block h-4 w-4" />
          <span className="text-xs text-slate-400 dark:text-slate-500">-</span>
        </div>
      ),
      items: (
        <button
          onClick={() => {
            navigate(`/menu/items?category=${category.id}`);
          }}
          className={`text-xs font-medium transition ${
            itemCount > 0
              ? "text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
              : "text-slate-500 dark:text-slate-400"
          }`}
          disabled={itemCount === 0}
        >
          {itemCount > 0
            ? `View ${itemCount} item${itemCount !== 1 ? "s" : ""}`
            : "0 items"}
        </button>
      ),
      sort: (
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            value={getCategorySortInputValue(category)}
            onChange={(e) =>
              handleCategorySortInputChange(category.id, e.target.value)
            }
            className="h-8 w-20 rounded-md border border-slate-300 px-2 text-xs text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            aria-label={`Sort order for ${category.name}`}
          />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleSaveCategorySort(category)}
            disabled={Boolean(savingSortByCategory[category.id])}
          >
            Save
          </Button>
        </div>
      ),
      status: (
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            category.isActive
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
          }`}
        >
          {category.isActive ? "Active" : "Inactive"}
        </span>
      ),
    };
  });

  if (!data && !isLoading) {
    return (
      <div className="space-y-3">
        <Card>
          <div className="mb-3">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              Categories
            </h1>
          </div>
          <Alert tone="warning">
            Unable to load categories. Please try again.
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sortSuccessMessage && (
        <div className="fixed bottom-4 right-4 z-50 w-[min(92vw,28rem)] shadow-xl">
          <Alert
            tone="success"
            className="border-emerald-200 bg-emerald-50/95 backdrop-blur"
            onClose={() => {
              if (sortSuccessTimeoutRef.current) {
                clearTimeout(sortSuccessTimeoutRef.current);
                sortSuccessTimeoutRef.current = null;
              }
              setSortSuccessMessage("");
            }}
          >
            {sortSuccessMessage}
          </Alert>
        </div>
      )}

      {/* Deprecated in Edge: category create/edit form */}
      {false && showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {editingId ? "Edit Category" : "Add Category"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Image Upload Section */}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Category Image
              </label>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Category preview"
                      className="h-24 w-24 rounded-lg border border-slate-200 dark:border-slate-700 object-cover"
                    />
                  ) : (
                    <div className="flex h-24 w-24 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800">
                      <svg
                        className="h-8 w-8 text-slate-400 dark:text-slate-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="mb-2 text-xs text-slate-600 dark:text-slate-400">
                    PNG or JPG, max 2MB, max 1024x1024px
                  </p>
                  <div className="flex gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <span className="inline-flex h-9 items-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-sm font-semibold text-slate-700 dark:text-slate-300 transition hover:border-slate-300 dark:hover:border-slate-500">
                        Choose File
                      </span>
                    </label>
                    {imagePreview && !imageFile && editingId && (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setImagePreview(null);
                          setRemoveImage(true);
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="Category Name"
                name="name"
                placeholder="Appetizers"
                value={form.name}
                onChange={handleChange}
                required
              />
              <Input
                label="Sort Order"
                name="sort"
                type="number"
                placeholder="0"
                value={form.sort}
                onChange={handleChange}
                helper="Lower numbers appear first"
              />
              <Select
                label="Parent Category"
                name="parentCategoryId"
                value={form.parentCategoryId}
                onChange={handleChange}
                helper="Optional: Create a subcategory"
              >
                <option value="">None (Top Level)</option>
                {parentCategories
                  .filter((cat) => cat.id !== editingId)
                  .sort((a, b) => a.sort - b.sort)
                  .map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
              </Select>
            </div>

            {stationFeatureActive && (
              <Select
                label="Default Station"
                name="defaultStationCode"
                value={form.defaultStationCode}
                onChange={handleChange}
                helper="Optional: default station for items in this category."
              >
                <option value="">No Default Station</option>
                {activeStations
                  .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
                  .map((station) => (
                    <option key={station.stationId} value={station.stationCode}>
                      {station.displayName} ({station.stationCode})
                    </option>
                  ))}
              </Select>
            )}

            {printTokensByStation && activeStations.length === 0 && (
              <Alert tone="warning">
                Print Tokens By Station is enabled, but there are no active
                stations. Create/activate a station to enable station assignment.
              </Alert>
            )}

            <Textarea
              label="Description"
              name="description"
              placeholder="Delicious starters to begin your meal"
              value={form.description}
              onChange={handleChange}
            />

            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
                className="h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-[#4f46e5] focus:ring-[#4f46e5] dark:bg-slate-700"
              />
              Active
            </label>

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Category {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {getStationErrorMessage(
                  createState.error || updateState.error,
                  "Failed to save category.",
                )}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Category
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <>
          <div className="mb-2 flex items-center gap-3 px-1">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-800 dark:text-slate-100">
              Categories
            </h1>
            <div className="ml-auto w-full max-w-md">
              <label className="relative block">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400 dark:text-slate-500">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-4.35-4.35m1.6-4.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                />
              </label>
            </div>
          </div>
          <Card className="p-0 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm rounded-xl overflow-hidden">
          <div className="px-2 py-2 md:px-3">
            {isLoading && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading categories...
              </p>
            )}
            {isError && (
              <Alert tone="error">
                {getCatalogOwnershipMessage(
                  error,
                  "Failed to load categories. Catalog is managed in Central Admin.",
                )}
              </Alert>
            )}
            {!isLoading && !isError && (
              <Table
                columns={columns}
                rows={rows}
                emptyText="No categories synced yet. Create categories in Central Admin and wait for Edge sync."
              />
            )}
          </div>
          </Card>
        </>
      )}

      {/* Deprecated in Edge: category delete modal */}
      {false && (
        <Modal
          isOpen={deleteModal.isOpen}
          onClose={() =>
            setDeleteModal({ isOpen: false, categoryId: null, categoryName: "" })
          }
          title="Delete Category"
          confirmText="Delete"
          confirmTone="danger"
          onConfirm={confirmDelete}
        >
          <p>
            Are you sure you want to delete{" "}
            <strong>{deleteModal.categoryName}</strong>?
          </p>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            This action cannot be undone. The category cannot be deleted if it has
            items.
          </p>
        </Modal>
      )}

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p>{errorModal.message}</p>
      </Modal>
    </div>
  );
};

export default Categories;

